import React, { useState } from "react";
import {
  Popover,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import Deck from "../../shared/api/DeckApi";
import { useAuth0 } from "@auth0/auth0-react";
import { t } from "i18next";
import { publicWebUrl } from "../../shared/runtime";

interface CreateDeckConfigMenuProps {
  selectedDeckIds: string[];
  anchorEl: HTMLButtonElement | null;
  open: boolean;
  onClose: () => void;
  lobbyName?: string;
  authUserName?: string;
}

export const CreateDeckConfigMenu: React.FC<CreateDeckConfigMenuProps> = ({
  selectedDeckIds,
  anchorEl,
  open,
  onClose,
  lobbyName,
  authUserName,
}) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [configName, setConfigName] = useState("");
  const [creatorName, setCreatorName] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill creator name when menu opens
  React.useEffect(() => {
    if (open) {
      // Priority: auth user name > lobby name > empty
      const prefilled = authUserName || lobbyName || "";
      setCreatorName(prefilled);
    }
  }, [open, authUserName, lobbyName]);

  const handleCreateLink = async () => {
    if (!configName.trim() || selectedDeckIds.length === 0) {
      setError(t("config_error_name"));
      return;
    }

    if (!creatorName.trim()) {
      setError(t("config_error_your_name"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let token: string | undefined;
      if (isAuthenticated) {
        token = await getAccessTokenSilently();
      }

      const result = await Deck.createDeckConfiguration(
        token,
        configName,
        selectedDeckIds,
        creatorName.trim()
      );

      if (result && result.code) {
        setGeneratedCode(result.code);
        setConfigName("");
        setCreatorName("");
      } else {
        setError(t("config_error_failed"));
      }
    } catch (e) {
      setError("Failed to create configuration");
    } finally {
      setLoading(false);
    }
  };

  const generatedLink = generatedCode
    ? publicWebUrl(`/config/${generatedCode}`)
    : "";

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      // Optional: show toast "Copied to clipboard"
    }
  };

  const handleReset = () => {
    setGeneratedCode(null);
    setConfigName("");
    setError(null);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{ sx: { padding: 2, minWidth: "300px", backgroundColor: "rgba(255, 255, 255, 0.98)" } }}
    >
      {generatedCode ? (
        <Box sx={{ textAlign: "center", padding: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t("config_link_created")}
          </Typography>
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              padding: 1,
              borderRadius: 1,
              wordBreak: "break-all",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <Typography variant="caption" sx={{ flex: 1 }}>
              {generatedLink}
            </Typography>
            <IconButton size="small" onClick={handleCopyLink}>
              <FileCopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={onClose}
          >
            {t("done")}
          </Button>
        </Box>
      ) : (
        <Box sx={{ padding: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t("config_create_link_subtitle")}
          </Typography>
          <TextField
            fullWidth
            label={t("config_your_name")}
            placeholder={t("config_name_placeholder")}
            value={creatorName}
            onChange={(e) => setCreatorName(e.target.value)}
            disabled={loading}
            size="small"
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("config_config_name")}
            placeholder={t("config_config_placeholder")}
            value={configName}
            onChange={(e) => setConfigName(e.target.value)}
            disabled={loading}
            size="small"
            margin="normal"
          />
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
          <Box sx={{ marginTop: 2 }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleCreateLink}
              disabled={loading || !configName.trim() || !creatorName.trim()}
            >
              {loading ? <CircularProgress size={24} /> : t("config_create_link")}
            </Button>
          </Box>
        </Box>
      )}
    </Popover>
  );
};

export default CreateDeckConfigMenu;
